import CurrencyLogo from 'components/CurrencyLogo'
import { MouseoverTooltip } from 'components/Tooltip'
import { useSortableTable } from 'hooks/analytics/useSortableTable'
import { useAllTokens } from 'hooks/Tokens'
import { memo, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { WrappedTokenInfo } from 'state/lists/wrappedTokenInfo'
import { UnifiedPoolData } from 'types/analytics'
import { formatDollarAmount } from 'utils/formatDollarAmt'
import { formatPercentage } from 'utils/numbers'
import { isMobile } from 'utils/userAgent'

import { POOLS_TABLE_ROW_HEIGHT, PoolsTableSkeletonRow, SkeletonBar } from '../Skeletons'
import TablePagination from './Pagination'
import { SortIndicator } from './SortIndicator'

interface PoolTableProps {
  poolDatas?: UnifiedPoolData[]
  isLoading?: boolean
  maxItems?: number
  forceLoading?: boolean
  versionView?: 'v2' | 'v3'
}

const feeTierPercent = (feeTier: number): string => `${(feeTier / 10000).toFixed(2)}%`

enum SortField {
  feeTier = 'feeTier',
  volumeUSD = 'volumeUSD',
  feesUSD = 'feesUSD',
  tvlUSD = 'tvlUSD',
  volumeUSDWeek = 'volumeUSDWeek',
  feesUSDWeek = 'feesUSDWeek',
  APR = 'APR',
}

const numericComparator = <T extends UnifiedPoolData>(selector: (item: T) => number | undefined) => {
  return (a: T, b: T) => (selector(a) ?? 0) - (selector(b) ?? 0)
}

const PoolsTableComponent = ({
  poolDatas,
  isLoading = false,
  maxItems = 20,
  forceLoading = false,
  versionView,
}: PoolTableProps) => {
  const allTokens = useAllTokens()
  const tokenCatalog = useMemo(
    () =>
      Object.values(allTokens).reduce<Record<string, WrappedTokenInfo>>((acc, token) => {
        acc[token.address.toLowerCase()] = token
        return acc
      }, {}),
    [allTokens]
  )

  const comparators = useMemo(
    () => ({
      [SortField.feeTier]: numericComparator<UnifiedPoolData>((pool) => pool.feeTier),
      [SortField.volumeUSD]: numericComparator<UnifiedPoolData>((pool) => pool.volumeUSD),
      [SortField.feesUSD]: numericComparator<UnifiedPoolData>((pool) => pool.feesUSD),
      [SortField.tvlUSD]: numericComparator<UnifiedPoolData>((pool) => pool.tvlUSD),
      [SortField.volumeUSDWeek]: numericComparator<UnifiedPoolData>((pool) => pool.volumeUSDWeek),
      [SortField.feesUSDWeek]: numericComparator<UnifiedPoolData>((pool) => pool.feesUSDWeek),
      [SortField.APR]: numericComparator<UnifiedPoolData>((pool) => pool.APR),
    }),
    []
  )

  const metricsReady = useMemo(
    () => Array.isArray(poolDatas) && poolDatas.every((pool) => pool?.metrics24hLoaded ?? pool?.metricsLoaded ?? false),
    [poolDatas]
  )

  const {
    pageItems: visiblePools,
    page,
    maxPage,
    sortField,
    sortDirection,
    handleSort,
    goToPreviousPage,
    goToNextPage,
  } = useSortableTable<UnifiedPoolData, SortField>({
    data: metricsReady ? poolDatas : undefined,
    defaultSortField: SortField.volumeUSD,
    comparators,
    pageSize: maxItems,
  })
  const hasData = metricsReady && Array.isArray(poolDatas) && poolDatas.length > 0
  const showSkeleton =
    forceLoading || poolDatas === undefined || (isLoading && !hasData) || (!metricsReady && Boolean(poolDatas?.length))

  const linkBaseClass = 'flex h-full w-full text-inherit no-underline'
  const handleMobileNavigate = useCallback(() => {
    if (isMobile) {
      setTimeout(() => window.scrollTo({ top: 0, left: 0 }), 0)
    }
  }, [])

  if (!showSkeleton && !hasData) {
    return (
      <div className="w-full rounded-lg border border-gray-800 bg-gray-900/50 p-8">
        <div className="text-center text-gray-400">No Pools Found</div>
      </div>
    )
  }

  return (
    <div className="w-full rounded-lg border border-gray-800 bg-gray-900/50 overflow-x-auto">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-gray-800">
            <tr className="text-gray-400 text-xs md:text-sm max-h-13">
              <th className="text-center px-2 py-3 md:py-4 font-medium w-12 md:w-16">#</th>
              <th className="text-left px-2 py-3 md:py-4 font-medium">
                <button
                  onClick={() => handleSort(SortField.feeTier)}
                  className="hover:text-gray-200 transition-colors cursor-pointer"
                >
                  Pool
                  <SortIndicator field={SortField.feeTier} activeField={sortField} direction={sortDirection} />
                </button>
              </th>
              <th className="text-right py-3 md:py-4 font-medium whitespace-nowrap">
                <button
                  onClick={() => handleSort(SortField.tvlUSD)}
                  className="hover:text-gray-200 transition-colors flex items-center justify-end w-full gap-0 md:gap-1 cursor-pointer"
                >
                  TVL
                  <SortIndicator field={SortField.tvlUSD} activeField={sortField} direction={sortDirection} />
                </button>
              </th>

              <th className="text-right py-3 md:py-4 font-medium cursor-pointer whitespace-nowrap">
                <button
                  onClick={() => handleSort(SortField.volumeUSD)}
                  className="hover:text-gray-200 transition-colors flex items-end justify-end w-full gap-0 md:gap-1 cursor-pointer"
                >
                  Volume 24H
                  <SortIndicator field={SortField.volumeUSD} activeField={sortField} direction={sortDirection} />
                </button>
              </th>
              <th className="text-right py-3 md:py-4 font-medium whitespace-nowrap">
                <MouseoverTooltip text="Fees paid by traders in the last 24 hours" placement="top">
                  <button
                    onClick={() => handleSort(SortField.feesUSD)}
                    className="hover:text-gray-200 transition-colors flex items-center justify-end w-full gap-0 md:gap-1 cursor-pointer"
                  >
                    Fees 24H
                    <SortIndicator field={SortField.feesUSD} activeField={sortField} direction={sortDirection} />
                  </button>
                </MouseoverTooltip>
              </th>
              <th className="text-right py-3 md:py-4 font-medium whitespace-nowrap">
                <button
                  onClick={() => handleSort(SortField.volumeUSDWeek)}
                  className="hover:text-gray-200 transition-colors flex items-center justify-end w-full gap-0 md:gap-1 cursor-pointer"
                >
                  Volume 7D
                  <SortIndicator field={SortField.volumeUSDWeek} activeField={sortField} direction={sortDirection} />
                </button>
              </th>
              <th className="text-right py-3 md:py-4 font-medium whitespace-nowrap">
                <MouseoverTooltip text="Fees paid by traders in the last 7 days" placement="top">
                  <button
                    onClick={() => handleSort(SortField.feesUSDWeek)}
                    className="hover:text-gray-200 transition-colors flex items-center justify-end w-full gap-0 md:gap-1 cursor-pointer"
                  >
                    Fees 7D
                    <SortIndicator field={SortField.feesUSDWeek} activeField={sortField} direction={sortDirection} />
                  </button>
                </MouseoverTooltip>
              </th>
              <th className="text-right py-3 md:py-4 font-medium whitespace-nowrap">
                <MouseoverTooltip text={`Annualized based on "Fees 24H"`} placement="top">
                  <button
                    onClick={() => handleSort(SortField.APR)}
                    className="hover:text-gray-200 transition-colors flex items-center justify-end w-full gap-0 md:gap-1 cursor-pointer md:w-max"
                    type="button"
                  >
                    <span className="text-right">APR</span>
                    <SortIndicator field={SortField.APR} activeField={sortField} direction={sortDirection} />
                  </button>
                </MouseoverTooltip>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {showSkeleton
              ? Array.from({ length: maxItems }).map((_, index) => (
                  <PoolsTableSkeletonRow key={`pool-skeleton-${index}`} />
                ))
              : visiblePools.map((pool, index) => {
                  const globalIndex = (page - 1) * maxItems + index + 1
                  const metrics24hReady = pool.metrics24hLoaded ?? pool.metricsLoaded ?? false
                  const metrics7dReady = pool.metrics7dLoaded ?? pool.metricsLoaded ?? false
                  const poolLink = `/explore/${versionView}/pools/${pool.id}`
                  const ariaLabel = `View pool ${pool.token0.symbol}/${pool.token1.symbol}`

                  return (
                    <tr
                      key={index}
                      className="hover:bg-gray-800/30 transition-colors cursor-pointer text-kodiak-text-primary text-base hover:text-kodiak-text-muted"
                      style={{ height: POOLS_TABLE_ROW_HEIGHT }}
                      aria-label={ariaLabel}
                    >
                      <td className="px-2 py-3 md:p-4 text-center w-12 md:w-16 text-sm md:text-base">
                        <Link
                          onClick={handleMobileNavigate}
                          to={poolLink}
                          aria-label={ariaLabel}
                          className={`${linkBaseClass} items-center justify-center`}
                        >
                          {globalIndex}
                        </Link>
                      </td>
                      <td className="px-2 py-3 min-w-45 md:p-4 md:w-[280px]">
                        <Link
                          onClick={handleMobileNavigate}
                          to={poolLink}
                          className={`${linkBaseClass} items-center gap-2 md:gap-3`}
                        >
                          <div className="flex -space-x-2">
                            {[pool.token0.address, pool.token1.address].map((address, logoIndex) => {
                              const tokenInfo = tokenCatalog[address.toLowerCase()]
                              const key = `${pool.id}-${logoIndex}`
                              return (
                                <CurrencyLogo
                                  key={key}
                                  currency={tokenInfo}
                                  size="28px"
                                  className="rounded-full border-2 border-gray-800 bg-gray-900 object-cover"
                                />
                              )
                            })}
                          </div>
                          <div className="flex flex-col gap-2 md:flex-row max-w-[290px]">
                            <span className="text-xs md:text-base font-medium max-w-[110px] md:max-w-[160px] overflow-hidden text-ellipsis whitespace-nowrap">
                              {pool.token0.symbol}/{pool.token1.symbol}
                            </span>
                            <span className="px-2 py-0.5 font-medium text-[0.65rem] md:text-xs rounded bg-gray-700 max-w-max flex items-center">
                              {feeTierPercent(pool.feeTier)}
                            </span>
                          </div>
                        </Link>
                      </td>
                      <td className="px-2 py-3 md:p-4 text-right">
                        <Link
                          onClick={handleMobileNavigate}
                          to={poolLink}
                          className={`${linkBaseClass} items-center justify-end`}
                        >
                          <div className="text-sm md:text-base font-medium tabular-nums self-start md:self-end">
                            {formatDollarAmount(pool.tvlUSD)}
                          </div>
                        </Link>
                      </td>

                      <td className="px-2 py-3 md:p-4 text-right">
                        <Link
                          onClick={handleMobileNavigate}
                          to={poolLink}
                          className={`${linkBaseClass} items-center justify-end`}
                        >
                          <div className="text-sm md:text-base font-medium tabular-nums self-start md:self-end">
                            {metrics24hReady ? (
                              formatDollarAmount(pool.volumeUSD)
                            ) : (
                              <SkeletonBar width="20" className="md:ml-auto" />
                            )}
                          </div>
                        </Link>
                      </td>
                      <td className="px-2 py-3 md:p-4 text-right">
                        <Link
                          onClick={handleMobileNavigate}
                          to={poolLink}
                          className={`${linkBaseClass} items-center justify-end`}
                        >
                          <div className="text-sm md:text-base font-medium tabular-nums self-start md:self-end">
                            {metrics24hReady ? (
                              formatDollarAmount(pool.feesUSD)
                            ) : (
                              <SkeletonBar width="20" className="md:ml-auto" />
                            )}
                          </div>
                        </Link>
                      </td>

                      <td className="px-2 py-3 md:p-4 text-right">
                        <Link
                          onClick={handleMobileNavigate}
                          to={poolLink}
                          className={`${linkBaseClass} items-center justify-end`}
                        >
                          <div className="text-sm md:text-base font-medium tabular-nums self-start md:self-end">
                            {metrics7dReady ? (
                              formatDollarAmount(pool.volumeUSDWeek)
                            ) : (
                              <SkeletonBar width="22" className="md:ml-auto" />
                            )}
                          </div>
                        </Link>
                      </td>
                      <td className="px-2 py-3 md:p-4 text-right">
                        <Link
                          onClick={handleMobileNavigate}
                          to={poolLink}
                          className={`${linkBaseClass} items-center justify-end`}
                        >
                          <div className="text-sm md:text-base font-medium tabular-nums self-start md:self-end">
                            {metrics7dReady ? (
                              formatDollarAmount(pool.feesUSDWeek)
                            ) : (
                              <SkeletonBar width="22" className="md:ml-auto" />
                            )}
                          </div>
                        </Link>
                      </td>
                      <td className="px-2 py-3 md:p-4 text-right">
                        <Link
                          onClick={handleMobileNavigate}
                          to={poolLink}
                          className={`${linkBaseClass} items-center justify-end`}
                        >
                          <div className="text-sm md:text-base font-medium tabular-nums self-start md:self-end">
                            {metrics24hReady && (pool.APR || pool.APR === 0) ? (
                              formatPercentage(pool.APR.toString())
                            ) : (
                              <SkeletonBar className="md:ml-auto" width="14" />
                            )}
                          </div>
                        </Link>
                      </td>
                    </tr>
                  )
                })}
          </tbody>
        </table>
      </div>

      <TablePagination page={page} maxPage={maxPage} onPrev={goToPreviousPage} onNext={goToNextPage} />
    </div>
  )
}

const PoolsTable = memo(PoolsTableComponent)

PoolsTable.displayName = 'PoolsTable'

export default PoolsTable
